import { useApiQuery, useAuth } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../api'

/** Legacy hardcoded internal-account exemption (`momentum-edit.component.ts`,
 * `indicatorcondition-edit.component.ts`, `coinpairlist-edit.component.ts` —
 * identical string in all three): not a security boundary, just a UI
 * affordance that skips the payment-method nag for internal accounts. Not a
 * secret — the domain is already public in the legacy source this ports. */
const INTERNAL_EMAIL_DOMAIN = '@webbandflow.co.uk'

/** `-1` while the credit-balance check is loading, failed, or answered with
 * something unusable (no `credit` row / bad shape): it stays `-1` rather than
 * falsely allowing/blocking edits. */
export type CreditEditGate = -1 | 0 | 1

export interface CreditGateState {
  /** `-1` loading/unknown, `0` blocked (negative credit balance), `1` allowed. */
  canEdit: CreditEditGate
  /** Whether a payment method still needs to be set up before saving. */
  paymentNeeded: boolean
  /** Whether the payment-method check has resolved (legacy `paymentMethodLoaded`). */
  paymentMethodLoaded: boolean
}

/** The currency whose balance gates editing. */
const GATE_CURRENCY = 'credit'

/** Parses one balance value: FeeSubscription emits a JSON number
 * (`legacy_number(Decimal)`), but a decimal string is accepted too. Anything
 * else (missing, non-finite, empty string) is unusable -> `null`. */
function parseBalance(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** Extracts the `credit` balance from the FeeSubscription credits response
 * (`GET /payment/credits` -> FS `GET /projects/{id}/credits`): an array of
 * `{ userId, currency, balance, createdAt, lastModAt, transactions }` that
 * ALWAYS contains a `credit` row (FS adds a transient zero row when the
 * account has none). `searchCreditInfos` returns `unknown` in lib 0.4.1, so
 * the shape is checked here; no usable `credit` row -> `null` (gate stays
 * unknown, never falsely allowing or blocking). */
export function creditBalanceFrom(data: unknown): number | null {
  if (!Array.isArray(data)) return null
  for (const row of data) {
    if (typeof row !== 'object' || row === null) continue
    const { currency, balance } = row as { currency?: unknown; balance?: unknown }
    if (currency === GATE_CURRENCY) return parseBalance(balance)
  }
  return null
}

function isPaymentMethodSetUp(value: unknown): boolean {
  return typeof value === 'object' && value !== null && Boolean((value as { setup?: unknown }).setup)
}

/**
 * Port of the per-page credit-balance edit gate duplicated across legacy
 * `MomentumEditComponent`/`IndicatorconditionEditComponent`/
 * `CoinpairlistEditComponent` (identical `ngOnInit` blocks, audit §6):
 * editing is blocked once the account's credit balance goes negative.
 * The balance is read from FeeSubscription (`GET /payment/credits`, the
 * `credit` row; `paymentClient.searchCreditInfos()`), NOT from the screener's
 * local ledger (`GET /credits/credit`, which only the retired direct-Stripe
 * path ever fed, so it was always 0 and the gate never blocked; BE 0.10.0),
 * and separately requires a payment method on file (`paymentNeeded`) —
 * unless the account is an internal `@webbandflow.co.uk` account, which
 * legacy always exempted from both checks.
 */
export function useCreditGate(): CreditGateState {
  const { user } = useAuth()
  const isInternalAccount = Boolean(user?.username?.includes(INTERNAL_EMAIL_DOMAIN))

  const paymentMethodQuery = useApiQuery(() => paymentClient.viewPaymentMethod(), [])
  const creditQuery = useApiQuery(() => paymentClient.searchCreditInfos(), [])

  const paymentMethodLoaded = paymentMethodQuery.data != null
  const paymentMethodSetUp = isPaymentMethodSetUp(paymentMethodQuery.data)
  const paymentNeeded = !isInternalAccount && !paymentMethodSetUp

  let canEdit: CreditEditGate = -1
  const balance = creditBalanceFrom(creditQuery.data)
  if (balance !== null) {
    canEdit = balance >= 0 ? 1 : 0
  }

  return { canEdit, paymentNeeded, paymentMethodLoaded }
}
